import React, { useState, useEffect, useMemo } from 'react'
import {
  buildExclusionValueList,
  buildExclusionColumnOptions,
  buildHashedColumnExclusionDisplayValues,
  filterExclusionValues,
  getRegisteredExclusionValuesForColumn,
  hasExclusionConflictWithPrimaryKey,
  isHashTargetColumn,
  isRegisteredHashTargetColumn
} from '../extra/helpers'
import SearchableDropdown from './SearchableDropdown'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'
import { DATA_CATEGORY } from '../extra/constants'
import { FILE_STATE } from '../extra/types.js'
import '../extra/types.js'

/**
 * @typedef {Object} DetailSettingsProps
 * @property {import('../extra/types').FileState} file
 * @property {string} fileKey
 * @property {import('../extra/types').IDConversionTable[]} idConversionTables
 * @property {Record<string, string>} searchKeyword
 * @property {function(Record<string, string>): void} onSearchKeywordChange
 * @property {function(string, any): void} onUpdateFile
 * @property {function(string, string, string): Promise<Set<string>>} onGetUniqueValues - (fileKey, columnName,
 *   sheetName)
 * @property {boolean} [readOnly] ウイルスチェック中等、編集不可のとき true
 * @property {boolean} [isDetailSettingsLockedByMapping] マッピング設定でファイル種別が使用中のとき true
 * @property {number[]} [exclusionValueErrorIndices] 登録時バリデーションで除外値未選択となった条件インデックス
 */

/**
 * DetailSettingsコンポーネント
 *
 * @param {DetailSettingsProps} props
 */
const DetailSettings = ({
  file,
  fileKey,
  idConversionTables,
  searchKeyword,
  onSearchKeywordChange,
  onUpdateFile,
  onGetUniqueValues,
  readOnly = false,
  isDetailSettingsLockedByMapping = false,
  exclusionValueErrorIndices = []
}) => {
  const [uniqueValuesMap, setUniqueValuesMap] = useState({})
  const [loadingColumns, setLoadingColumns] = useState(new Set())

  /** 値のトグルでは変わらない。カラム構成の変化とアコーディオン再展開時の再取得トリガーに使う */
  const exclusionColumnSignature = useMemo(
    () => (file.exclusionConditions || []).map((c) => c.column ?? '').join('\x1e'),
    [file.exclusionConditions]
  )

  // アコーディオン再展開でコンポーネントが再マウントされたとき、設定済みカラムのユニーク値を復元する
  useEffect(() => {
    let cancelled = false
    const conditions = file.exclusionConditions || []

    const loadUniqueValuesForConditions = async () => {
      for (let index = 0; index < conditions.length; index++) {
        const columnName = conditions[index]?.column
        if (!columnName) {
          if (!cancelled) {
            setUniqueValuesMap((prev) => {
              const next = { ...prev }
              delete next[index]
              return next
            })
          }
          continue
        }

        setLoadingColumns((prev) => new Set(prev).add(index))
        try {
          const uniqueValues = await onGetUniqueValues(fileKey, columnName, file.sheetName ?? '')
          if (cancelled) return
          setUniqueValuesMap((prev) => ({
            ...prev,
            [index]: uniqueValues
          }))
        } catch (error) {
          console.error('Failed to get unique values:', error)
        } finally {
          setLoadingColumns((prev) => {
            const next = new Set(prev)
            next.delete(index)
            return next
          })
        }
      }
    }

    loadUniqueValuesForConditions()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exclusionConditions の値変更では再取得しない
  }, [exclusionColumnSignature, fileKey, file.sheetName, file.contentRevision, onGetUniqueValues])

  const fieldLocked = readOnly || !file.fileClassification
  const isPendingVirusCheck = file.fileState === FILE_STATE.PENDING_VIRUS_CHECK
  // マッピング設定使用中ロック（⑦）のみファイル変更後（pending-virus-check）は解除する。
  // ファイル種別未選択ロック（①）は登録前後を問わず常に有効（種別に紐づかない詳細設定を作らせないため）
  const effectiveFieldLocked = fieldLocked || (isDetailSettingsLockedByMapping && !isPendingVirusCheck)
  const regCols = file.registeredHashColumns || []
  const showRegisteredHashNotice = regCols.length > 0 && !isDetailSettingsLockedByMapping
  const hasRegisteredExclusionValues = (file.registeredExclusionConditions || []).some(
    (c) => c.column && (c.values || []).length > 0
  )
  const showRegisteredExclusionNotice = hasRegisteredExclusionValues && !isDetailSettingsLockedByMapping

  // 従業員ID変換表はキー項目設定済みかつデータ種別「従業員別」の場合のみ活性
  // （変換表はキー項目の従業員ID変換にのみ使用するため。値のクリアは FileCard 側のハンドラで行う）
  const isConversionTableDisabled =
    effectiveFieldLocked || !file.primaryKeyColumn || file.dataCategory !== DATA_CATEGORY.EMPLOYEE

  // ID変換テーブルの選択肢
  const idConversionOptions = useMemo(() => {
    return [
      { value: '', label: '変換しない' },
      ...idConversionTables.map((table) => ({
        value: table.id,
        label: table.name
      }))
    ]
  }, [idConversionTables])

  // 選択中のID変換テーブル
  const selectedTable = useMemo(() => {
    if (!file.idConversionTable) return null
    return idConversionTables.find((table) => table.id === file.idConversionTable)
  }, [file.idConversionTable, idConversionTables])

  // 除外条件の追加
  const handleAddExclusionCondition = () => {
    if (effectiveFieldLocked) return
    const newConditions = [...(file.exclusionConditions || []), { column: '', values: [] }]
    onUpdateFile(fileKey, { exclusionConditions: newConditions })
  }

  // 除外条件の削除
  const handleRemoveExclusionCondition = (index) => {
    if (effectiveFieldLocked) return
    const cond = (file.exclusionConditions || [])[index]
    if (getRegisteredExclusionValuesForColumn(file.registeredExclusionConditions, cond?.column).length > 0) {
      return
    }
    const newConditions = file.exclusionConditions.filter((_, idx) => idx !== index)
    onUpdateFile(fileKey, { exclusionConditions: newConditions })
  }

  // 除外条件のカラム変更（ユニーク値の取得は exclusionColumnSignature の useEffect に委譲）
  const handleExclusionColumnChange = (index, columnName) => {
    if (effectiveFieldLocked) return
    if (columnName && columnName === file.primaryKeyColumn) return
    const cond = (file.exclusionConditions || [])[index]
    if (getRegisteredExclusionValuesForColumn(file.registeredExclusionConditions, cond?.column).length > 0) {
      return
    }
    const newConditions = (file.exclusionConditions || []).map((cond, idx) => {
      if (idx === index) {
        return {
          column: columnName,
          values: []
        }
      }
      return cond
    })
    onUpdateFile(fileKey, { exclusionConditions: newConditions })
  }

  // ハッシュ化列のトグル（registeredHashColumns に含まれる列は変更不可のためここでは触らない）
  const handleToggleHashColumn = (columnName) => {
    if (effectiveFieldLocked) return
    const registered = file.registeredHashColumns || []
    if (registered.includes(columnName)) return
    const current = file.hashColumns || []
    const next = current.includes(columnName) ? current.filter((c) => c !== columnName) : [...current, columnName]
    onUpdateFile(fileKey, { hashColumns: next })
  }

  // 除外条件の値のトグル（registeredExclusionConditions に含まれる値は解除不可）
  const handleToggleExclusionValue = (index, value) => {
    if (effectiveFieldLocked) return
    const cond = (file.exclusionConditions || [])[index]
    const registeredValues = getRegisteredExclusionValuesForColumn(file.registeredExclusionConditions, cond?.column)
    if (registeredValues.includes(value) && (cond?.values || []).includes(value)) {
      return
    }
    const newConditions = (file.exclusionConditions || []).map((cond, idx) => {
      if (idx === index) {
        // 条件オブジェクトとvalues配列の両方を新しいオブジェクトとして作成
        const currentValues = [...(cond.values || [])]
        const valueIndex = currentValues.indexOf(value)

        if (valueIndex === -1) {
          // 値を追加
          return {
            ...cond,
            values: [...currentValues, value]
          }
        } else {
          // 値を削除
          return {
            ...cond,
            values: currentValues.filter((v) => v !== value)
          }
        }
      }
      // 他の条件はそのまま返す
      return cond
    })

    onUpdateFile(fileKey, { exclusionConditions: newConditions })
  }

  // 検索キーワードの変更
  const handleSearchKeywordChange = (index, keyword) => {
    if (effectiveFieldLocked) return
    onSearchKeywordChange({
      ...searchKeyword,
      [index]: keyword
    })
  }

  // フィルタリングされた除外値
  const getFilteredExclusionValues = (index, condition, registeredValuesForColumn) => {
    const keyword = searchKeyword[index] || ''
    if (isRegisteredHashTargetColumn(condition.column, file.registeredHashColumns)) {
      return buildHashedColumnExclusionDisplayValues(registeredValuesForColumn, condition.values || [], keyword)
    }
    const uniqueValues = uniqueValuesMap[index] || new Set()
    return buildExclusionValueList(filterExclusionValues(uniqueValues, keyword))
  }

  // 設定済み除外条件のサマリー
  const activeConditions = useMemo(() => {
    return (file.exclusionConditions || []).filter((c) => c.column && c.values.length > 0)
  }, [file.exclusionConditions])

  const hasPrimaryKeyExclusionConflict = hasExclusionConflictWithPrimaryKey(file)

  /** 除外対象カラムの選択肢（キー項目・他条件で選択済みのカラムは除外。現在値は表示用に残す） */
  const getExclusionColumnOptions = (conditionIndex, currentColumn) => {
    return buildExclusionColumnOptions(
      file.headers || [],
      file.primaryKeyColumn,
      file.exclusionConditions || [],
      conditionIndex,
      currentColumn
    )
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-300">
      {isDetailSettingsLockedByMapping && !file.isUploadedLocally && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
          このファイル種別はマッピング設定で使用中のため、従業員ID変換表・データ除外条件・ハッシュ化対象列は変更できません。
          <br />
          設定を変更する場合は、汎用マッピング画面で、このファイル種別を使っているマッピングをすべて解除するか、「ファイル変更」からファイルを差し替えてください。
        </div>
      )}

      {/* ID変換テーブルセクション */}
      <div
        className={`mb-6 p-3.5 rounded-md border-2 ${
          selectedTable ? 'bg-blue2-lighten-50 border-blue2' : 'bg-amber-50 border-amber-400'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span>🔄</span>
          <span className={`text-h6 font-semibold ${selectedTable ? 'text-blue2-darken-20' : 'text-amber-700'}`}>
            従業員ID変換表
          </span>
        </div>
        <div className="text-xs text-gray-600 mb-3">
          システムごとに異なる従業員IDを統一するための変換ルールを指定します。
        </div>
        <SearchableDropdown
          options={idConversionOptions}
          value={file.idConversionTable || ''}
          onChange={(value) => {
            if (!isConversionTableDisabled) onUpdateFile(fileKey, { idConversionTable: value })
          }}
          placeholder="変換しない"
          disabled={isConversionTableDisabled}
          className="w-full"
        />
        {selectedTable && (
          <div className="mt-2 p-3 bg-white rounded text-xs text-blue2">
            📝 {selectedTable.description} ({selectedTable.recordCount.toLocaleString()}件)
          </div>
        )}
      </div>

      {/* 除外条件セクション */}
      <div
        className={`p-3.5 rounded-md border-2 ${
          activeConditions.length > 0 ? 'bg-red-50 border-red-500' : 'bg-red-50 border-red-300'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span>🚫</span>
          <span className="text-h6 font-semibold text-red-700">データ除外条件</span>
        </div>
        <div className="text-xs text-gray-600 mb-3">
          特定のカラムに指定した値が入っているデータをマッピング対象から除外します。
        </div>
        {showRegisteredExclusionNotice && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
            登録済みのファイルでは、登録時に指定した除外値のチェック解除はできません。新しい値の追加は可能です。
          </div>
        )}
        {hasPrimaryKeyExclusionConflict && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
            キー項目として選択した「{file.primaryKeyColumn}
            」はデータ除外対象に指定できません。キー項目を変更するか、除外条件の対象カラムを変更してください。
          </div>
        )}

        {/* 設定済み除外条件のサマリー */}
        {activeConditions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {activeConditions.map((cond, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-800 rounded text-xs"
              >
                🚫 {cond.column} = {cond.values.map((v) => (v === '' ? '(空)' : v)).join(', ')}
              </span>
            ))}
          </div>
        )}

        {/* 除外条件リスト */}
        <div className="space-y-3 mb-3">
          {(file.exclusionConditions || []).map((condition, index) => {
            const registeredValuesForColumn = getRegisteredExclusionValuesForColumn(
              file.registeredExclusionConditions,
              condition.column
            )
            const isExclusionConditionLocked = registeredValuesForColumn.length > 0
            const isColumnHashTarget = isHashTargetColumn(
              condition.column,
              file.hashColumns,
              file.registeredHashColumns
            )
            const isColumnHashRegistered = isRegisteredHashTargetColumn(condition.column, file.registeredHashColumns)
            const hasExclusionValues = registeredValuesForColumn.length > 0 || (condition.values || []).length > 0
            const hideExclusionValueSelector = isColumnHashRegistered && !hasExclusionValues
            return (
              <div key={index} className="bg-white border border-gray-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-red-600">除外条件 {index + 1}</span>
                  <button
                    type="button"
                    className="btn red white--text !py-1 px-3 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleRemoveExclusionCondition(index)}
                    disabled={effectiveFieldLocked || isExclusionConditionLocked}
                  >
                    削除
                  </button>
                </div>

                {/* カラム選択 */}
                <div className="mb-3">
                  <label className="text-xs text-gray-700 font-medium mb-1 block">対象カラム</label>
                  <SearchableDropdown
                    options={getExclusionColumnOptions(index, condition.column || '')}
                    value={condition.column || ''}
                    onChange={(value) => handleExclusionColumnChange(index, value)}
                    placeholder="-- 対象カラムを選択 --"
                    disabled={effectiveFieldLocked || isExclusionConditionLocked}
                    className="w-full"
                  />
                </div>

                {/* 値選択エリア */}
                {condition.column && (
                  <div
                    id={
                      exclusionValueErrorIndices.includes(index)
                        ? `file-management-exclusion-value-error-${fileKey}-${index}`
                        : undefined
                    }
                    className={`bg-gray-50 border rounded-md p-2.5 ${
                      exclusionValueErrorIndices.includes(index) ? 'border-red-500' : 'border-gray-200'
                    }`}
                  >
                    <div className="text-xs text-gray-600 mb-2 font-medium">除外する値を選択（複数選択可）</div>
                    {isColumnHashTarget && (
                      <div className="mb-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                        ハッシュ化された項目は非表示になり、除外対象として設定できません。
                      </div>
                    )}
                    {exclusionValueErrorIndices.includes(index) && (
                      <div className="text-caption-2 red--text mb-2">除外する値を1件以上選択してください</div>
                    )}
                    {!hideExclusionValueSelector && (
                      <>
                        <input
                          type="text"
                          className="text-input text-left w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs mb-2"
                          placeholder="値を検索..."
                          value={searchKeyword[index] || ''}
                          onChange={(e) => handleSearchKeywordChange(index, e.target.value)}
                          readOnly={effectiveFieldLocked}
                          disabled={effectiveFieldLocked}
                        />
                        {loadingColumns.has(index) ? (
                          <div className="text-xs text-gray-500">読み込み中...</div>
                        ) : (
                          <div className="max-h-[120px] overflow-y-auto border border-gray-200 rounded p-2.5 bg-white">
                            <div className="flex flex-wrap gap-1.5">
                              {(() => {
                                const allValues = getFilteredExclusionValues(
                                  index,
                                  condition,
                                  registeredValuesForColumn
                                )
                                return allValues.map((value) => {
                                  const displayValue = value === '' ? '(空)' : value
                                  const isSelected = condition.values.includes(value)
                                  const isValueDisabled = effectiveFieldLocked
                                  const isValueRegistered = isSelected && registeredValuesForColumn.includes(value)
                                  const canToggleValue =
                                    !isValueDisabled && !isValueRegistered && !isColumnHashRegistered
                                  return (
                                    <div
                                      key={value}
                                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs transition-all select-none ${
                                        !canToggleValue ? 'cursor-default opacity-80' : 'cursor-pointer'
                                      } ${
                                        isSelected
                                          ? isValueRegistered
                                            ? 'bg-red-400 text-white border border-red-400'
                                            : 'bg-red-600 text-white border border-red-600'
                                          : 'bg-white border border-gray-300 hover:border-red-500 hover:bg-red-50'
                                      }`}
                                      onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (canToggleValue) handleToggleExclusionValue(index, value)
                                      }}
                                      role="checkbox"
                                      aria-checked={isSelected}
                                      aria-disabled={isValueRegistered}
                                      tabIndex={canToggleValue ? 0 : -1}
                                      onKeyDown={(e) => {
                                        if (!canToggleValue) return
                                        if (e.key === ' ' || e.key === 'Enter') {
                                          e.preventDefault()
                                          handleToggleExclusionValue(index, value)
                                        }
                                      }}
                                    >
                                      <span
                                        className={`w-3.5 h-3.5 flex items-center justify-center rounded border ${
                                          isSelected ? 'bg-white border-white' : 'bg-white border-gray-400'
                                        }`}
                                      >
                                        {isSelected && <span className="text-red-600 text-[10px] font-bold">✓</span>}
                                      </span>
                                      <span>{displayValue}</span>
                                    </div>
                                  )
                                })
                              })()}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* 条件追加ボタン（仕様8: ファイル種別未選択時は非活性） */}
        <button
          type="button"
          className="btn red white--text !py-2 px-4 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleAddExclusionCondition}
          disabled={effectiveFieldLocked}
        >
          <span>+</span> 条件を追加
        </button>
      </div>

      {/* ハッシュ化対象列セクション */}
      <div className="mt-6 p-3.5 rounded-md border-2 bg-gray-50 border-gray-400">
        <div className="flex items-center gap-2 mb-2">
          <span>🔒</span>
          <span className="text-h6 font-semibold text-gray-700">ハッシュ化対象列の選択</span>
        </div>
        <div className="text-xs text-gray-600 mb-3">
          個人情報等のセンシティブな情報を含む列を選択してください。選択した列はサーバ上でハッシュ化され、マッピング設定では使用できません。
        </div>
        {showRegisteredHashNotice && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
            登録済みのファイルでは、登録時に指定したハッシュ化対象列のチェック解除はできません。新しい列の追加は可能です。
          </div>
        )}
        {(() => {
          const hashCols = file.hashColumns || []
          const allHashCols = [...new Set([...hashCols, ...regCols])]
          if (allHashCols.length === 0) return null
          return (
            <div className="flex flex-wrap gap-2 mb-4">
              {allHashCols.map((col) => (
                <span
                  key={col}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs ${
                    regCols.includes(col) ? 'bg-gray-300 text-gray-700' : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  🔒 {col}
                  {regCols.includes(col) && '（登録済み）'}
                </span>
              ))}
            </div>
          )
        })()}
        <div className="flex flex-wrap gap-2">
          {(file.headers || [])
            .filter((header) => header !== file.primaryKeyColumn)
            .map((header) => {
              const isRegistered = (file.registeredHashColumns || []).includes(header)
              const isChecked = isRegistered || (file.hashColumns || []).includes(header)
              return (
                <label
                  key={header}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs border transition-all ${
                    effectiveFieldLocked
                      ? 'opacity-50 cursor-not-allowed'
                      : isRegistered
                        ? 'bg-gray-100 border-gray-300 cursor-default'
                        : 'cursor-pointer bg-white border-gray-300 hover:border-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={effectiveFieldLocked || isRegistered}
                    onChange={() => handleToggleHashColumn(header)}
                    className="rounded border-gray-400"
                  />
                  <span>{header}</span>
                </label>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default DetailSettings
